import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI, setSessionExpiredHandler, finishReauth } from '../services/api';
import WorkTypeToggle from './WorkTypeToggle';

// V70.224 — Oturum süresi dolunca sayfa yenilenmez: bu pencereden yeniden giriş yapılır,
// açık sayfa / arama sonuçları / sepet yerinde kalır, bekleyen istekler tekrar gönderilir.
const tokenUserId = (t) => {
  try { return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).id; } catch { return null; }
};

const toLogin = () => {
  localStorage.removeItem('mtu_token');
  localStorage.removeItem('mtu_user');
  window.location.href = '/login';
};

const SessionExpiredModal = () => {
  const { user, loading, completeLogin } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [workType, setWorkType] = useState(''); // V71.3.1 — her girişte zorunlu (Ofis / Remote)
  const [pending, setPending] = useState(null); // { token, email, maskedEmail }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const active = !!user && !loading;

  useEffect(() => {
    if (!active) return undefined;
    setSessionExpiredHandler(() => setOpen(true));
    return () => { setSessionExpiredHandler(null); finishReauth(false); };
  }, [active]);

  const close = useCallback(() => {
    setOpen(false); setPassword(''); setWorkType(''); setPending(null); setCode(''); setError('');
    finishReauth(true);
  }, []);

  // Başka sekmede aynı kullanıcıyla yeniden girildiyse pencereyi kapat
  useEffect(() => {
    if (!open) return undefined;
    const onStorage = (e) => {
      if (e.key !== 'mtu_token' || !e.newValue) return;
      if (tokenUserId(e.newValue) !== user?.id) window.location.reload();
      else close();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [open, user, close]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      let data;
      if (!pending) {
        data = (await authAPI.login({ username: user.username, password, rememberMe, work_type: workType })).data;
        if (data.mfa_required || data.email_otp_required) {
          setPending({ token: data.pending_token, email: !!data.email_otp_required, maskedEmail: data.masked_email || '' });
          return;
        }
      } else {
        const body = { pending_token: pending.token, code };
        data = (await (pending.email ? authAPI.verifyEmailOtp(body) : authAPI.verifyMFA(body))).data;
      }
      // 2FA kurulumu / şifre değişikliği / farklı hesap → normal giriş akışı
      if (!data.token || !data.user || data.user.id !== user.id || data.must_change_password) return toLogin();
      completeLogin(data.token, data.user);
      close();
    } catch (err) {
      const d = err.response?.data || {};
      const msg = d.error || d.errors?.[0]?.msg || 'Giriş başarısız';
      setError(msg);
      if (pending && /Tekrar giriş/i.test(msg)) { setPending(null); setCode(''); }
    } finally {
      setBusy(false);
    }
  };

  if (!open || !active) return null;

  return (
    <div style={S.overlay}>
      <form style={S.modal} onSubmit={submit} role="dialog" aria-modal="true" aria-label="Oturum süresi doldu">
        <div style={S.header}>
          <h2 style={S.title}>🔒 Oturum süresi doldu</h2>
          <p style={S.subtitle}>Açık sayfanız, arama sonuçlarınız ve sepetiniz korunuyor. Kaldığınız yerden devam etmek için yeniden giriş yapın.</p>
        </div>
        <div style={S.body}>
          {error && <div style={S.error}>{error}</div>}
          {!pending ? (
            <>
              <div className="form-group">
                <label className="form-label">Kullanıcı</label>
                <input className="form-input" value={user.full_name ? `${user.full_name} (${user.username})` : user.username} disabled />
              </div>
              <div className="form-group">
                <label className="form-label">Şifre</label>
                <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" autoComplete="current-password" autoFocus required />
              </div>
              <WorkTypeToggle value={workType} onChange={setWorkType} />
              <label style={S.remember}>
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
                Beni hatırla (8 saat)
              </label>
            </>
          ) : (
            <div className="form-group">
              <label className="form-label">
                {pending.email ? `E-postanıza gelen kod${pending.maskedEmail ? ` (${pending.maskedEmail})` : ''}` : 'Authenticator kodu veya yedek kod'}
              </label>
              <input key={pending.token} className="form-input" value={code} onChange={e => setCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 9))}
                autoComplete="one-time-code" autoFocus style={S.code} />
            </div>
          )}
        </div>
        <div style={S.footer}>
          <button type="button" style={S.linkBtn} onClick={toLogin}>Giriş ekranına git</button>
          <button type="submit" className="btn btn-primary" disabled={busy || (pending ? code.length < 6 : (!password || !workType))}>
            {busy ? 'Doğrulanıyor...' : pending ? 'Doğrula' : 'Giriş Yap'}
          </button>
        </div>
      </form>
    </div>
  );
};

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'var(--bg-overlay, rgba(15,23,42,0.55))', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000,
  },
  modal: { background: 'var(--bg-elevated, #fff)', color: 'var(--fg-primary)', borderRadius: 16, width: 420, maxWidth: '92vw', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' },
  header: { background: 'var(--grad-brand)', color: '#fff', padding: '20px 24px 16px' },
  title: { margin: 0, fontSize: 19, fontWeight: 700 },
  subtitle: { margin: '6px 0 0', fontSize: 13, opacity: 0.85, lineHeight: '19px' },
  body: { padding: '18px 24px 4px' },
  error: { background: 'var(--danger-50)', color: 'var(--danger-700)', border: '1px solid var(--danger-200)', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 14 },
  remember: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--fg-secondary, #64748b)', cursor: 'pointer', userSelect: 'none', marginBottom: 6 },
  code: { fontSize: 20, letterSpacing: 4, textAlign: 'center' },
  footer: { padding: '12px 24px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--fg-secondary, #64748b)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 },
};

export default SessionExpiredModal;
